import { useState, useEffect, useRef, useCallback, FC } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  DEV_SCORING_FORMS__KEY,
  LOG_POST_MESSAGES__KEY,
  LOG_WEB_SOCKET_MESSAGES__KEY,
  LOG_READING_WS_MESSAGES__KEY,
  LOG_SCORING_FORM_EMITTER__KEY,
  LOG_REDUX_ACTIONS__KEY,
  SKIP_LOGOUT_TIMER__KEY,
  DICOM_CACHE__KEY,
  DICOM_CACHE_STORAGE_NAME,
} from '@dynamika-shared/utils/development';
import { OHIFViewerModeType } from '@dynamika-shared/types/viewerMode';

const PANEL_POSITION__KEY = 'DEV_PANEL_POSITION';
const FAB_SIZE = 40;
const EDGE_PADDING = 8;
const DRAG_THRESHOLD_PX = 4;

type PanelPosition = { x: number; y: number };

type ToggleItem = {
  key: string;
  label: string;
};

type Section = {
  title: string;
  items: ToggleItem[];
};

const SECTIONS: Section[] = [
  {
    title: 'Dev flags',
    items: [
      { key: DEV_SCORING_FORMS__KEY, label: 'Scoring Forms (dev)' },
      { key: SKIP_LOGOUT_TIMER__KEY, label: 'Skip logout timer' },
      { key: DICOM_CACHE__KEY, label: 'DICOM cache' },
    ],
  },
  {
    title: 'Logging',
    items: [
      { key: LOG_POST_MESSAGES__KEY, label: 'PostMessages' },
      { key: LOG_WEB_SOCKET_MESSAGES__KEY, label: 'WebSocket' },
      { key: LOG_READING_WS_MESSAGES__KEY, label: 'Reading WS' },
      { key: LOG_SCORING_FORM_EMITTER__KEY, label: 'ScoringFormEmitter' },
      { key: LOG_REDUX_ACTIONS__KEY, label: 'Redux actions' },
    ],
  },
];

const ALL_FLAG_KEYS = SECTIONS.flatMap(s => s.items).map(item => item.key);

const OHIF_VERSIONS = ['v3.9.0', 'v3.11.0'];

const readFlag = (key: string) => window.localStorage.getItem(key) === '1';

const readAllFlags = () =>
  ALL_FLAG_KEYS.reduce<Record<string, boolean>>((acc, key) => {
    acc[key] = readFlag(key);
    return acc;
  }, {});

const toggleFlag = (key: string) => {
  const next = readFlag(key) ? '0' : '1';
  window.localStorage.setItem(key, next);
};

const readStoredPosition = (): PanelPosition | null => {
  try {
    const raw = window.localStorage.getItem(PANEL_POSITION__KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PanelPosition;
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const getDefaultPosition = (): PanelPosition => ({ x: window.innerWidth / 2, y: 8 });

const clampPosition = (pos: PanelPosition): PanelPosition => ({
  x: Math.min(
    Math.max(EDGE_PADDING, pos.x),
    window.innerWidth - FAB_SIZE - EDGE_PADDING
  ),
  y: Math.min(
    Math.max(EDGE_PADDING, pos.y),
    window.innerHeight - FAB_SIZE - EDGE_PADDING
  ),
});

type DevFloatingPanelProps = {
  mode?: OHIFViewerModeType;
};

const readCurrentVersion = (mode?: OHIFViewerModeType) => {
  const modeKey = mode
    ? window.localStorage.getItem(`OHIFVersion_${mode}`)
    : null;
  return modeKey ?? window.localStorage.getItem('OHIFVersion') ?? 'v3.9.0';
};

const DevFloatingPanelInner: FC<DevFloatingPanelProps> = ({ mode }) => {
  const [position, setPosition] = useState<PanelPosition>(() =>
    clampPosition(readStoredPosition() ?? getDefaultPosition())
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [flags, setFlags] = useState<Record<string, boolean>>(readAllFlags);

  const currentVersion = readCurrentVersion(mode);

  const dragStateRef = useRef<{
    startClientX: number;
    startClientY: number;
    startPosX: number;
    startPosY: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    const onResize = () => setPosition(prev => clampPosition(prev));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const state = dragStateRef.current;
    if (!state) return;
    const dx = event.clientX - state.startClientX;
    const dy = event.clientY - state.startClientY;
    if (!state.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      state.moved = true;
    }
    if (state.moved) {
      setPosition(
        clampPosition({ x: state.startPosX + dx, y: state.startPosY + dy })
      );
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    const state = dragStateRef.current;
    if (!state) return;
    const wasDrag = state.moved;
    dragStateRef.current = null;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);

    setPosition(prev => {
      const clamped = clampPosition(prev);
      if (wasDrag) {
        window.localStorage.setItem(
          PANEL_POSITION__KEY,
          JSON.stringify(clamped)
        );
      }
      return clamped;
    });

    if (!wasDrag) {
      setIsExpanded(prev => !prev);
    }
  }, [handleMouseMove]);

  useEffect(
    () => () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    },
    [handleMouseMove, handleMouseUp]
  );

  useEffect(() => {
    if (!isExpanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isExpanded]);

  const handleFabMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      dragStateRef.current = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPosX: position.x,
        startPosY: position.y,
        moved: false,
      };
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [position.x, position.y, handleMouseMove, handleMouseUp]
  );

  const handleFabKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsExpanded(prev => !prev);
    }
  }, []);

  const [dicomCacheCleared, setDicomCacheCleared] = useState(false);

  const handleToggleFlag = (key: string) => {
    toggleFlag(key);
    setFlags(readAllFlags());
  };

  /**
   * The viewer iframe is served from this application's own origin
   * (`${window.location.origin}/viewer/<version>/`), so the cache it fills sits
   * in the same storage this button can reach — no postMessage needed.
   */
  const handleClearDicomCache = async () => {
    await window.caches?.delete(DICOM_CACHE_STORAGE_NAME);

    setDicomCacheCleared(true);
    window.setTimeout(() => setDicomCacheCleared(false), 1500);
  };

  const handleToggleAll = () => {
    ALL_FLAG_KEYS.forEach(key => toggleFlag(key));
    setFlags(readAllFlags());
  };

  const handleSetOHIFVersion = (version: string) => {
    if (mode) {
      window.localStorage.setItem(`OHIFVersion_${mode}`, version);
    }
    window.localStorage.setItem('OHIFVersion', version);
    window.location.reload();
  };

  const openLeft = position.x > window.innerWidth / 2;
  const openUp = position.y > window.innerHeight / 2;

  return (
    <div
      style={{ left: position.x, top: position.y }}
      className='fixed z-[9999] select-none'
      data-testid='dev-floating-panel'
    >
      <button
        type='button'
        onMouseDown={handleFabMouseDown}
        onKeyDown={handleFabKeyDown}
        className={twMerge(
          'flex h-5 w-5 items-center justify-center rounded-full text-white shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-300',
          isExpanded
            ? 'bg-orange-600 hover:bg-orange-700'
            : 'bg-red-600 hover:bg-red-700'
        )}
        title='Dev tools (drag to move, click to expand)'
        aria-label='Open dev tools panel'
        aria-expanded={isExpanded}
        aria-controls='dev-floating-panel-menu'
      >
        <svg
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='white'
          stroke='white'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
        >
          <circle cx='12' cy='7' r='1.5' />
          <rect x='9' y='8' width='6' height='8' rx='1' />
          <path d='M9 9 L4 5' fill='none' />
          <path d='M9 11 L3 10' fill='none' />
          <path d='M9 13 L3 14' fill='none' />
          <path d='M9 15 L4 19' fill='none' />
          <path d='M15 9 L20 5' fill='none' />
          <path d='M15 11 L21 10' fill='none' />
          <path d='M15 13 L21 14' fill='none' />
          <path d='M15 15 L20 19' fill='none' />
        </svg>
      </button>

      {isExpanded && (
        <div
          id='dev-floating-panel-menu'
          role='menu'
          style={{
            [openLeft ? 'right' : 'left']: '0',
            [openUp ? 'bottom' : 'top']: `${FAB_SIZE + 8}px`,
          }}
          className='absolute w-[220px] rounded-md border border-gray-700 bg-gray-900/95 p-2 text-xs text-white shadow-2xl backdrop-blur'
          onMouseDown={e => e.stopPropagation()}
        >
          <div className='mb-2'>
            <div className='mb-1 flex items-baseline justify-between text-[9px] font-semibold uppercase tracking-wider text-gray-400'>
              <span>OHIF Version{mode ? ` — ${mode}` : ''}</span>
              <span className='normal-case text-gray-500'>{currentVersion}</span>
            </div>
            <div className='flex gap-1'>
              {OHIF_VERSIONS.map(v => (
                <button
                  key={v}
                  type='button'
                  onClick={() => handleSetOHIFVersion(v)}
                  className={twMerge(
                    'flex-1 rounded px-1 py-0.5 text-[11px] font-medium transition-colors',
                    currentVersion === v
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {SECTIONS.map(section => (
            <div key={section.title} className='mb-2 last:mb-0'>
              <div className='mb-0.5 text-[9px] font-semibold uppercase tracking-wider text-gray-400'>
                {section.title}
              </div>
              <div className='flex flex-col'>
                {section.items.map(item => {
                  const isOn = flags[item.key];
                  return (
                    <button
                      key={item.key}
                      type='button'
                      onClick={() => handleToggleFlag(item.key)}
                      className='flex items-center justify-between gap-2 rounded px-1 py-0.5 text-left text-[11px] hover:bg-gray-800'
                    >
                      <span className='truncate'>{item.label}</span>
                      <span
                        className={twMerge(
                          'flex h-3 w-5 shrink-0 items-center rounded-full transition-colors',
                          isOn ? 'bg-emerald-500' : 'bg-gray-600'
                        )}
                      >
                        <span
                          className={twMerge(
                            'h-2 w-2 rounded-full bg-white transition-transform',
                            isOn ? 'translate-x-2.5' : 'translate-x-0.5'
                          )}
                        />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className='mt-2 border-t border-gray-700 pt-1.5'>
            <button
              type='button'
              onClick={handleToggleAll}
              className='w-full rounded bg-amber-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-amber-700'
            >
              Toggle all flags
            </button>
            <button
              type='button'
              onClick={handleClearDicomCache}
              className='mt-1 w-full rounded bg-gray-700 px-2 py-0.5 text-[11px] font-medium text-gray-200 hover:bg-gray-600'
            >
              {dicomCacheCleared ? 'DICOM cache cleared' : 'Clear DICOM cache'}
            </button>
            <div className='mt-1 text-[9px] text-gray-500'>
              Drag to reposition. Position saved to localStorage.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const DevFloatingPanel: FC<DevFloatingPanelProps> = props => {
  if (window.location.hostname !== 'localhost') {
    return null;
  }
  return <DevFloatingPanelInner {...props} />;
};
